import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { Toaster } from './components/ui/sonner';
import { Navbar } from './components/Navbar';
import Sidebar from './components/Sidebar';
import Footer from './components/Footer';
import OfflineIndicator from './components/OfflineIndicator';
import AgentSuspensionGuard from './components/AgentSuspensionGuard';
import FloatingAssistant from './components/FloatingAssistant';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { RegionProvider } from './context/RegionContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { setupAuthInterceptors, isTokenExpired, clearSession } from './utils/authSession';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import Dashboard from './pages/Dashboard';
import Stores from './pages/Stores';
import CreateStore from './pages/CreateStore';
import StoreDashboard from './pages/StoreDashboard';
import BecomeAgent from './pages/BecomeAgent';
import AgentTasks from './pages/AgentTasks';
import MyCompanies from './pages/MyCompanies';
import CompanyDetail from './pages/CompanyDetail';
import PayInvoice from './pages/PayInvoice';
import PaymentGateway from './pages/PaymentGateway';
import AgentTaskChat from './pages/AgentTaskChat';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancelled from './pages/PaymentCancelled';
import Account from './pages/Account';
import Payments from './pages/Payments';
import ServiceRequestsList from './pages/ServiceRequestsList';
import ServiceRequestDetail from './pages/ServiceRequestDetail';
import AdminWithdrawals from './pages/AdminWithdrawals';
import AdminSupport from './pages/AdminSupport';
import BusinessAiChat from './pages/BusinessAiChat';
import PrivacyPolicy from './pages/PrivacyPolicy';
import AboutUs from './pages/AboutUs';
import TermsOfService from './pages/TermsOfService';
import UserDataDeletion from './pages/UserDataDeletion';
import ContactUs from './pages/ContactUs';
import MyRequests from './pages/MyRequests';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';

// Global axios auth interceptors (session expiry handling)
setupAuthInterceptors();

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check for user on initial load and when storage changes
  useEffect(() => {
    const checkUser = () => {
      try {
        const userData = localStorage.getItem('business_user');
        const token = localStorage.getItem('business_token');
        if (userData && token) {
          if (isTokenExpired(token)) {
            clearSession();
            setUser(null);
          } else {
            setUser(JSON.parse(userData));
          }
        } else {
          setUser(null);
        }
      } catch (e) {
        console.error('Error parsing user data:', e);
        setUser(null);
      }
      setLoading(false);
    };

    checkUser();

    // Listen for storage changes (for cross-tab sync)
    window.addEventListener('storage', checkUser);
    
    // Custom event for same-tab updates
    window.addEventListener('userLoggedIn', checkUser);
    window.addEventListener('userLoggedOut', checkUser);

    return () => {
      window.removeEventListener('storage', checkUser);
      window.removeEventListener('userLoggedIn', checkUser);
      window.removeEventListener('userLoggedOut', checkUser);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    // iter-302: smooth logout — change the URL to '/' FIRST so the
    // ProtectedRoute does not render Navigate->/login during tear-down.
    // Only after the route is swapped do we clear state + storage.
    if (window.location.pathname !== '/') {
      window.history.pushState({}, '', '/');
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
    localStorage.removeItem('business_token');
    localStorage.removeItem('business_user');
    setUser(null);
    window.dispatchEvent(new Event('userLoggedOut'));
  };

  const ProtectedRoute = ({ children }) => {
    // iter-302: use a theme-aware loader + fade to prevent the harsh white
    // flash observed between route changes during the initial auth check.
    if (loading) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] dark:bg-[#0B0F19] transition-colors">
          <div className="animate-spin h-8 w-8 border-4 border-[#38BDF8] border-t-transparent rounded-full" />
        </div>
      );
    }
    return user ? children : <Navigate to="/login" replace />;
  };

  // Layout component with Sidebar
  const AppLayout = ({ children, user, onLogout }) => {
    // Start with sidebar closed on mobile
    const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 1024);
    const location = useLocation();
    const { language } = useLanguage();
    const { isDarkMode } = useTheme();
    
    // Pages that should show sidebar (protected pages)
    const pagesWithSidebar = ['/dashboard', '/stores', '/account', '/become-agent', '/agent-tasks', '/payments', '/my-requests', '/service-requests'];
    // Full-screen pages (no sidebar even if a sidebar-eligible path is a prefix)
    const fullScreenPages = ['/agent-tasks/chat', '/support', '/payment-success', '/payment-cancelled'];
    const isFullScreen = fullScreenPages.some(p => location.pathname.startsWith(p));
    const showSidebar = user && !isFullScreen && pagesWithSidebar.some(page => location.pathname.startsWith(page));
    
    // Pages without footer
    const pagesWithoutFooter = ['/login', '/register', '/forgot-password', '/support', '/payment-success', '/payment-cancelled'];
    const showFooter = !pagesWithoutFooter.includes(location.pathname) && !location.pathname.startsWith('/support') && !location.pathname.startsWith('/agent-tasks/chat');
    
    return (
      <div className="flex flex-col">
        <Navbar 
          user={user} 
          onLogout={onLogout} 
          showSidebarToggle={showSidebar}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
        />
        <div className="flex flex-1 pt-16 sm:pt-20">
          {showSidebar && (
            <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
          )}
          <main 
            className={`flex-1 min-w-0 transition-all duration-300 ${
              showSidebar 
                ? sidebarOpen 
                  ? 'lg:ms-64' 
                  : 'lg:ms-20'
                : ''
            }`}
          >
            {children}
          </main>
        </div>
        {showFooter && <Footer />}
        {/* iter-301 — Floating AI assistant appears on every authenticated page. */}
        {user && !isFullScreen && (
          <FloatingAssistant language={language} isDarkMode={isDarkMode} />
        )}
      </div>
    );
  };

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <ErrorBoundary>
        <ThemeProvider>
          <LanguageProvider>
            <RegionProvider>
              <BrowserRouter>
                <ScrollToTop />
                <div className="App">
                  <AppLayout user={user} onLogout={handleLogout}>
                    <ErrorBoundary>
                      <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    {/* Public payment result pages — no auth required (customers arrive from payment gateway) */}
                    <Route path="/payment-success" element={<PaymentSuccess />} />
                    <Route path="/payment-cancelled" element={<PaymentCancelled />} />
                    <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                    <Route path="/stores" element={<ProtectedRoute><Stores /></ProtectedRoute>} />
                    <Route path="/stores/create" element={<ProtectedRoute><CreateStore /></ProtectedRoute>} />
                    <Route path="/stores/:storeId" element={<ProtectedRoute><StoreDashboard /></ProtectedRoute>} />
                    <Route path="/become-agent" element={<ProtectedRoute><BecomeAgent /></ProtectedRoute>} />
                    <Route path="/agent-tasks" element={<ProtectedRoute><AgentTasks /></ProtectedRoute>} />
                    <Route path="/agent-tasks/chat/:taskId" element={<ProtectedRoute><AgentTaskChat /></ProtectedRoute>} />
                    <Route path="/my-companies" element={<ProtectedRoute><MyCompanies /></ProtectedRoute>} />
                    {/* iter-287 — both /companies and /companies/:id supported;
                        the summary card uses /companies/:id, older links may
                        use /my-companies. */}
                    <Route path="/companies" element={<ProtectedRoute><MyCompanies /></ProtectedRoute>} />
                    <Route path="/companies/:id" element={<ProtectedRoute><CompanyDetail /></ProtectedRoute>} />
                    {/* iter-289 — Unified Payments Hub picker page */}
                    <Route path="/pay/:invoiceId" element={<ProtectedRoute><PayInvoice /></ProtectedRoute>} />
                    <Route path="/pay-gateway" element={<ProtectedRoute><PaymentGateway /></ProtectedRoute>} />
                    <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
                    <Route path="/support" element={<ProtectedRoute><BusinessAiChat /></ProtectedRoute>} />
                    <Route path="/ai-chat" element={<Navigate to="/support" replace />} />
                    <Route path="/payments" element={<ProtectedRoute><Payments /></ProtectedRoute>} />
                    <Route path="/service-requests" element={<ProtectedRoute><ServiceRequestsList /></ProtectedRoute>} />
                    <Route path="/service-requests/:id" element={<ProtectedRoute><ServiceRequestDetail /></ProtectedRoute>} />
                    <Route path="/admin/withdrawals" element={<ProtectedRoute><AdminWithdrawals /></ProtectedRoute>} />
                    <Route path="/admin/support" element={<ProtectedRoute><AdminSupport /></ProtectedRoute>} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/about-us" element={<AboutUs />} />
                    <Route path="/terms-of-service" element={<TermsOfService />} />
                    <Route path="/user-data-deletion" element={<UserDataDeletion />} />
                    <Route path="/data-deletion" element={<Navigate to="/user-data-deletion" replace />} />
                    <Route path="/contact-us" element={<ContactUs />} />
                    <Route path="/help" element={<Navigate to="/contact-us" replace />} />
                    <Route path="/my-requests" element={<ProtectedRoute><MyRequests /></ProtectedRoute>} />
                      </Routes>
                    </ErrorBoundary>
                  </AppLayout>
                  <OfflineIndicator />
                  <AgentSuspensionGuard />
                  <Toaster position="top-center" richColors />
                </div>
              </BrowserRouter>
            </RegionProvider>
        </LanguageProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </GoogleOAuthProvider>
  );
}

export default App;
